import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import { API_URL, storedData } from "./Constants";
import CryptoJS from "crypto-js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./FeeStatement.css";

export default function FeeStatement() {

  const location = useLocation();
  const authKey = location.state?.auth;   // 🔐 Only source of secret key

  const { company_id: paramCompanyId, parent_no: paramParentNo, sibling: paramSibling, date_filter } = useParams();

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const statementRef = useRef(null);

  // --- Decrypt user info ---
  let loggedUserData = null;
  if (storedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(storedData, authKey);
      loggedUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
      console.error("User data decryption error:", err);
    }
  }

  const company_id = paramCompanyId || loggedUserData?.company_id;
  const parent_no = paramParentNo || loggedUserData?.parent_no;
  const sibling = paramSibling;
  const [from, to] = date_filter ? date_filter.split("_") : [null, null];

  // --- Fetch Fee Statement ---
  useEffect(() => {
    if (!company_id || !parent_no || !sibling || !from || !to || !authKey) return;

    setLoading(true);

    axios.get(
      `${API_URL}/fee_statement/${company_id}/${parent_no}/${sibling}/${date_filter}`,
      {
        headers: {
          Authorization: `CustomToken ${authKey}`,   // 🔐 Use passed key
        },
      }
    )
    .then((res) => {
      if (res.data.success) setStatement(res.data);
    })
    .catch((err) => {
      console.error("❌ Error fetching statement:", err);
      alert("Error fetching statement.");
    })
    .finally(() => setLoading(false));

  }, [company_id, parent_no, sibling, date_filter, from, to, authKey]);

  const [downloading, setDownloading] = useState(false);



  // --- PDF Download Function ---
  const downloadPDF = async () => {
    if (!statement) return;
    setDownloading(true);

    const input = statementRef.current;
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 10, pageWidth, imgHeight);
    pdf.save(`FeeStatement_${statement.student.full_name}_${new Date().toISOString().split("T")[0]}.pdf`);

    setDownloading(false);
  };

  // --- No Student Selected ---
  if (!sibling)
    return (
      <div className="statement-container">
        <div className="no-selection">Please select a student from the Fee Statement filter.</div>
      </div>
    );

  // --- Totals ---
  const totalDebit = statement?.transactions
    ?.reduce((sum, t) => sum + parseFloat((t.debit || "0").toString().replace(/,/g, "")), 0)
    .toFixed(2);
  const totalCredit = statement?.transactions
    ?.reduce((sum, t) => sum + parseFloat((t.credit || "0").toString().replace(/,/g, "")), 0)
    .toFixed(2);
  const closingBalance = parseFloat((statement?.summary?.closing_balance || "0").toString().replace(/,/g, "")).toFixed(2);

return (
  <div className="statement-container container-fluid">
  <div className="statement-card card shadow-sm p-3 p-md-4">

    {/* Printable Content */}
    <div ref={statementRef}>

      {loading ? (
        <div className="text-center py-5">Loading...</div>
      ) : (
        statement?.success && (
          <>
            {/* ================= HEADER ================= */}
            <div className="report-header text-center mb-4">

              <div className="row align-items-center text-center text-md-start">

                {/* Logo Left */}
                <div className="col-12 col-md-3 mb-3 mb-md-0">
                  <img
                    src={statement.settings.logo_base64}
                    alt="Logo"
                    crossOrigin="anonymous"
                    className="img-fluid shadow-sm rounded"
                    style={{ maxHeight: "85px" }}
                  />
                </div>

                {/* School Info */}
                <div className="col-12 col-md-6">
                  <h4 className="fw-bold mb-1">
                    {statement.settings.organization}
                  </h4>

                  <p className="mb-1 small text-muted">
                    {statement.settings.address}
                  </p>

                  <p className="mb-1 small text-muted">
                    {statement.settings.organization_email} • {statement.settings.contacts}
                  </p>

                  <p className="small fst-italic text-muted">
                    <strong>Motto:</strong> {statement.settings.slogan} |{" "}
                    <strong>Vision:</strong> {statement.settings.vision}
                    <br />
                    <strong>Mission:</strong> {statement.settings.mission}
                  </p>
                </div>

                {/* Logo Right */}
                <div className="col-12 col-md-3">
                  {statement.settings.logo_base64 && (
                    <img
                      src={statement.settings.logo_base64}
                      alt="Logo"
                      className="img-fluid shadow-sm rounded"
                      style={{ maxHeight: "85px" }}
                    />
                  )}
                </div>

              </div>

              <hr className="mt-3" />
            </div>

            {/* ================= TITLE ================= */}
            <div className="text-center mb-4">
              <h4 className="fw-bold">📘 Fee Statement</h4>
            </div>

            {/* ================= STUDENT INFO ================= */}
            <div className="row mb-4 align-items-center">

              <div className="col-12 col-md-6">
                <h5 className="fw-semibold mb-1">
                  {statement.student.full_name}
                </h5>
                <p className="mb-0 text-muted small">
                  Adm No: {statement.student.admin_no}
                </p>
              </div>

              <div className="col-12 col-md-6 text-md-end mt-2 mt-md-0">
                <span className="badge bg-primary p-2">
                  Period: {from} → {to}
                </span>
              </div>

            </div>

            {/* ================= SUMMARY ================= */}
            <div className="row g-3 mb-4">

              <div className="col-6 col-md-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body">
                    <p className="small text-muted mb-1">Balance B/F</p>
                    <h6 className="fw-bold">{statement.balance_bf || 0}</h6>
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body">
                    <p className="small text-muted mb-1">Invoiced</p>
                    <h6 className="fw-bold">
                      {statement.summary.total_invoiced || 0}
                    </h6>
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body">
                    <p className="small text-muted mb-1">Paid</p>
                    <h6 className="fw-bold">
                      {statement.summary.total_paid || 0}
                    </h6>
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-3">
                <div className="card text-center shadow-sm h-100 border-success">
                  <div className="card-body">
                    <p className="small text-muted mb-1">Closing Balance</p>
                    <h6 className="fw-bold text-success">
                      {closingBalance}
                    </h6>
                  </div>
                </div>
              </div>

            </div>

            {/* ================= DESKTOP TABLE ================= */}
            <div className="d-none d-md-block table-responsive mb-4">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Document No</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th className="text-end">Debit</th>
                    <th className="text-end">Credit</th>
                    <th className="text-end">Balance</th>
                  </tr>
                </thead>

                <tbody>
                  {statement.transactions.map((t, i) => (
                    <tr key={i}>
                      <td>{t.date}</td>
                      <td>{t.document_no}</td>
                      <td>{t.transaction_type}</td>
                      <td>{t.description}</td>
                      <td className="text-end">{t.debit || 0}</td>
                      <td className="text-end">{t.credit || 0}</td>
                      <td className="text-end fw-bold">{t.balance || 0}</td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr>
                    <td colSpan="4" className="text-end fw-bold">
                      Totals
                    </td>
                    <td className="text-end fw-bold">{totalDebit}</td>
                    <td className="text-end fw-bold">{totalCredit}</td>
                    <td className="text-end fw-bold">{closingBalance}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ================= MOBILE TRANSACTIONS ================= */}
            <div className="d-block d-md-none mb-4">
              {statement.transactions.map((t, i) => (
                <div key={i} className="card mb-3 shadow-sm">
                  <div className="card-body">

                    <div className="d-flex justify-content-between mb-2">
                      <strong>{t.date}</strong>
                      <span className="fw-bold">{t.balance}</span>
                    </div>

                    <div className="small text-muted mb-1">
                      {t.transaction_type}
                    </div>

                    <div className="mb-2">{t.description}</div>

                    <div className="d-flex justify-content-between small">
                      <span>Debit: {t.debit || 0}</span>
                      <span>Credit: {t.credit || 0}</span>
                    </div>

                  </div>
                </div>
              ))}
            </div>
          </>
        )
      )}
    </div>

    {/* ================= DOWNLOAD BUTTON ================= */}
    {statement && (
      <div className="text-center mt-3">
        <button
          className="btn btn-primary px-4"
          onClick={downloadPDF}
          disabled={downloading}
        >
          {downloading ? "Generating..." : "⬇ Download PDF"}
        </button>
      </div>
    )}
  </div>
</div>
);

}
